import { ref, watch, onMounted, nextTick, type Ref, type ComputedRef } from 'vue';

/**
 * Shared composable for tab navigation with URL hash sync.
 * Extracts the common tab management pattern used across all viewer tab components.
 *
 * @param availableTabs - Computed array of available tab hashes (e.g., ['#summary', '#comments'])
 * @param tabsRoot - Native template ref for the viewer tabs root element.
 * @param defaultTab - Optional default tab to select. Falls back to first available tab.
 */
export const useTabNavigation = <T extends string>(availableTabs: ComputedRef<T[]>, tabsRoot: Ref<HTMLElement | null>, defaultTab?: T) => {
  const selectedTab: Ref<T> = ref((defaultTab || '') as T);

  // Reset selection when available tabs change
  watch(
    availableTabs,
    tabs => {
      if (!tabs.includes(selectedTab.value) && tabs.length > 0) {
        selectedTab.value = tabs[0];
      }
    },
    { immediate: true }
  );

  const scrollToSelectedTab = async (alwaysScroll = false) => {
    await nextTick();

    const tabsComponent = tabsRoot.value;

    if (!tabsComponent) {
      return;
    }

    const { top } = tabsComponent.getBoundingClientRect();
    const minimumVisibleContent = 120;
    const sectionNeedsFocus = top < 0 || top + minimumVisibleContent > window.innerHeight;

    if (alwaysScroll || sectionNeedsFocus) {
      tabsComponent.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Read an intentional hash deep link on mount without moving a clean viewer URL.
  onMounted(() => {
    const hash = window.location.hash as T;
    if (hash && availableTabs.value.includes(hash)) {
      selectedTab.value = hash;
      void scrollToSelectedTab();
    } else if (availableTabs.value.length > 0) {
      selectedTab.value = defaultTab && availableTabs.value.includes(defaultTab) ? defaultTab : availableTabs.value[0];
    }
  });

  const onTabClick = (tab: T) => {
    selectedTab.value = tab;
    window.location.hash = tab;
    void scrollToSelectedTab();
  };

  const onTabChange = (tab: T) => {
    selectedTab.value = tab;
  };

  const selectTab = (tab: T, { alwaysScroll = false }: { alwaysScroll?: boolean } = {}) => {
    if (availableTabs.value.includes(tab)) {
      selectedTab.value = tab;
      window.location.hash = tab;
      void scrollToSelectedTab(alwaysScroll);
    }
  };

  return {
    selectedTab: selectedTab as Ref<T>,
    onTabClick,
    onTabChange,
    selectTab,
  };
};
